import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Card,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { useEdgeGateways, useEdgeInstances, useInfiniteEdgeGatewayLogs } from '../api/hooks'
import { api, ApiError } from '../api/client'
import type { EdgeGateway, EdgeInstance, EdgeInstanceType } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { humanBytes, humanKb, humanizeSeconds, localDate, relativeTime, timeOfDay } from '../lib/format'
import { ConnDot } from './Gateways'
import { EnrollWizard } from '../components/EnrollWizard'
import { AllowedIpsField } from '../components/AllowedIpsField'
import { parseAllowedIpsInput } from '../lib/ipacl'
import { isManualInstanceType, isRetiredInstanceType } from '../lib/edgeInstances'
import { DoorHealthCard } from '../components/features/DoorHealthCard'

const INSTANCE_TYPES: { value: EdgeInstanceType; label: string; placeholder: string }[] = [
  {
    value: 'axema-ws',
    label: 'Axema WS',
    placeholder: '{"url": "wss://192.168.1.10/admin/websocket/", "source": "os-…", "channels": ["event"]}',
  },
  {
    value: 'http-relay',
    label: 'HTTP relay',
    placeholder: '{"allowlist": [{"host": "192.168.1.10", "ports": [443]}]}',
  },
]

// Display-only labels: materialized instances, agent worker types (the
// metrics `workers[]` list) and the retired acre-edp type.
const TYPE_LABELS: Record<string, string> = {
  stream_relay: 'Stream relay',
  axema_forwarder: 'Axema forwarder',
  'acre-edp': 'Acre EDP (retired)',
}

function instanceTypeLabel(type: string): string {
  return INSTANCE_TYPES.find((t) => t.value === type)?.label ?? TYPE_LABELS[type] ?? type
}

function isCreatableType(type: string | undefined): type is EdgeInstanceType {
  return INSTANCE_TYPES.some((t) => t.value === type)
}

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** Usage bar for mem/disk percentages; turns warn/danger at 80/95%. */
function UsageBar({ label, usedPct, detail }: { label: string; usedPct?: number; detail?: string }) {
  const pct = typeof usedPct === 'number' ? Math.min(100, Math.max(0, usedPct)) : null
  const tone = pct === null ? 'bg-ostrel-dim' : pct >= 95 ? 'bg-ostrel-danger' : pct >= 80 ? 'bg-ostrel-warn' : 'bg-ostrel-ok'
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">{label}</span>
        <span className="tnum text-xs text-ostrel-ink">
          {pct === null ? '—' : `${Math.round(pct)}%`}
          {detail && <span className="tnum ml-2 text-[10px] text-ostrel-ink-muted">{detail}</span>}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded-sm bg-ostrel-sand">
        {pct !== null && <div className={`h-1.5 rounded-sm ${tone}`} style={{ width: `${pct}%` }} />}
      </div>
    </div>
  )
}

const workerStateChips: Record<string, { tone: 'ok' | 'warn' | 'danger' | 'muted'; label: string }> = {
  active: { tone: 'ok', label: 'Active' },
  starting: { tone: 'warn', label: 'Starting' },
  disconnected: { tone: 'danger', label: 'Disconnected' },
}

/**
 * Last-known appliance telemetry (the metrics up-frame, latest-wins — no
 * history). Forward-compat: every key is optional, missing values render "—".
 */
function MetricsCard({ gateway }: { gateway: EdgeGateway }) {
  const m = gateway.metrics
  const workers = m?.workers ?? []

  return (
    <Card
      title="Metrics"
      action={
        gateway.metrics_at ? (
          <span className="tnum text-[11px] text-ostrel-ink-muted">
            Last reported {relativeTime(gateway.metrics_at)}
          </span>
        ) : undefined
      }
    >
      {!m ? (
        <EmptyState
          title="No telemetry yet"
          hint="The appliance reports metrics about once a minute while connected."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <dl className="flex flex-wrap gap-x-8 gap-y-2">
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">Uptime</dt>
              <dd className="tnum text-xs text-ostrel-ink">
                {typeof m.uptime_s === 'number' ? humanizeSeconds(m.uptime_s) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">Load average</dt>
              <dd className="tnum text-xs text-ostrel-ink">
                {m.loadavg && m.loadavg.length >= 3
                  ? `${m.loadavg[0].toFixed(2)} ${m.loadavg[1].toFixed(2)} ${m.loadavg[2].toFixed(2)}`
                  : '—'}
                {typeof m.cpu_count === 'number' && (
                  <span className="ml-2 text-[10px] text-ostrel-ink-muted">· {m.cpu_count} CPU</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">Spool</dt>
              <dd className="tnum text-xs text-ostrel-ink">
                {m.spool
                  ? `${m.spool.depth ?? 0} queued · ${humanBytes(m.spool.bytes ?? 0)}`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">Versions</dt>
              <dd className="tnum text-xs text-ostrel-ink">
                agent {m.agent || '—'} · python {m.python || '—'} · {m.kernel || '—'}
              </dd>
            </div>
          </dl>

          <div className="grid gap-3 border-t border-ostrel-line pt-3 sm:grid-cols-2">
            <UsageBar
              label="Memory"
              usedPct={m.mem?.used_pct}
              detail={
                typeof m.mem?.total_kb === 'number'
                  ? `${humanKb((m.mem.total_kb ?? 0) - (m.mem.available_kb ?? 0))} / ${humanKb(m.mem.total_kb)}`
                  : undefined
              }
            />
            <UsageBar
              label={m.disk?.path ? `Disk ${m.disk.path}` : 'Disk'}
              usedPct={m.disk?.used_pct}
              detail={
                typeof m.disk?.total_kb === 'number'
                  ? `${humanKb((m.disk.total_kb ?? 0) - (m.disk.free_kb ?? 0))} / ${humanKb(m.disk.total_kb)}`
                  : undefined
              }
            />
          </div>

          {workers.length > 0 && (
            <div className="border-t border-ostrel-line pt-3">
              <Table>
                <THead>
                  <TR>
                    <TH>Worker</TH>
                    <TH>Type</TH>
                    <TH>State</TH>
                  </TR>
                </THead>
                <TBody>
                  {workers.map((w, i) => {
                    const chip = workerStateChips[w.state ?? ''] ?? { tone: 'muted' as const, label: w.state ?? 'unknown' }
                    return (
                      <TR key={w.instance ?? i}>
                        <TD className="tnum font-mono text-xs text-ostrel-ink">{w.instance ?? '—'}</TD>
                        <TD className="text-xs text-ostrel-ink">{w.type ? instanceTypeLabel(w.type) : '—'}</TD>
                        <TD>
                          <Chip tone={chip.tone} label={chip.label} />
                        </TD>
                      </TR>
                    )
                  })}
                </TBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

/**
 * Edit the gateway's optional source-IP allowlist (PATCH /gateways/{uid}).
 * Empty = any source; the field validates CIDRs client-side (the backend
 * revalidates).
 */
function EditAllowedIpsModal({
  gateway,
  onClose,
}: {
  gateway: EdgeGateway
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const [aclText, setAclText] = useState(gateway.allowed_ips.join('\n'))

  const acl = useMemo(() => parseAllowedIpsInput(aclText), [aclText])

  const save = useMutation({
    mutationFn: () => api.updateGateway(gateway.unique_id, { allowed_ips: acl.cidrs }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope] })
      onClose()
    },
  })

  return (
    <Modal open title="Edit allowed source IPs" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (!acl.error && !save.isPending) save.mutate()
        }}
      >
        <AllowedIpsField value={aclText} onChange={setAclText} error={acl.error} />
        {save.error && (
          <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
            {errDetail(save.error)}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="navy" size="sm" disabled={!!acl.error || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Reinstall (re-enroll) flow: the enrollment wizard (artifact choice →
 * download + docs → gated token reveal) rotates the enroll token once an
 * available artifact is picked — POST /gateways/{uid}/re-enroll returns the
 * new plaintext token exactly once.
 */
function ReinstallGatewayModal({
  gatewayUid,
  onClose,
}: {
  gatewayUid: string
  onClose: () => void
}) {
  return (
    <Modal open title="Reinstall gateway" onClose={onClose}>
      <EnrollWizard
        intro={
          <p className="mb-2 text-xs leading-relaxed text-ostrel-ink-muted">
            Reinstalling replaces the appliance with a freshly installed machine under the SAME
            gateway identity — its instances, desired spec and log ring are all preserved. This is
            zero-downtime: the old machine keeps working until the new one enrolls with the rotated
            token. The current enroll token (if any) dies the moment a new one is minted — that
            happens when you pick an installer below.
          </p>
        }
        enroll={async () => (await api.reenrollGateway(gatewayUid)).enroll_token}
        onDone={onClose}
      />
    </Modal>
  )
}

/**
 * Add/edit instance form (mounted fresh per open, so state initializes from
 * `instance`). PUT is full-replace; `secrets` is always omitted here, so the
 * backend preserves any stored secrets (the systems.System secrets contract).
 */
function InstanceForm({
  gateway,
  instance,
  onDone,
}: {
  gateway: string
  instance: EdgeInstance | null
  onDone: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const [name, setName] = useState(instance?.name ?? '')
  // a retired/materialized type can't be saved back; default to a creatable one
  const initialType = instance?.type
  const [type, setType] = useState<EdgeInstanceType>(isCreatableType(initialType) ? initialType : 'http-relay')
  const [paramsText, setParamsText] = useState(
    instance && Object.keys(instance.params).length > 0 ? JSON.stringify(instance.params, null, 2) : '',
  )

  const paramsError = useMemo(() => {
    if (!paramsText.trim()) return null
    try {
      const parsed: unknown = JSON.parse(paramsText)
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return 'Params must be a JSON object.'
      }
      return null
    } catch {
      return 'Params must be valid JSON.'
    }
  }, [paramsText])

  const save = useMutation({
    mutationFn: () => {
      const params = paramsText.trim() ? (JSON.parse(paramsText) as Record<string, unknown>) : {}
      const body = { gateway, type, name: name.trim(), params }
      return instance
        ? api.updateEdgeInstance(instance.unique_id, body)
        : api.createEdgeInstance(body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope] })
      onDone()
    },
  })

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim() && !paramsError && !save.isPending) save.mutate()
      }}
    >
      <Input label="Instance name" value={name} onChange={(e) => setName(e.target.value)} required />
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="instance-type"
          className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted"
        >
          Type
        </label>
        <select
          id="instance-type"
          value={type}
          onChange={(e) => setType(e.target.value as EdgeInstanceType)}
          className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
        >
          {INSTANCE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="instance-params"
          className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted"
        >
          Params (JSON object, optional)
        </label>
        <textarea
          id="instance-params"
          value={paramsText}
          onChange={(e) => setParamsText(e.target.value)}
          rows={4}
          spellCheck={false}
          placeholder={INSTANCE_TYPES.find((t) => t.value === type)?.placeholder}
          className="tnum rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 font-mono text-xs text-ostrel-ink placeholder:text-ostrel-ink-muted focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
        />
        {paramsError && <p className="text-xs text-ostrel-danger">{paramsError}</p>}
      </div>
      {save.error && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(save.error)}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="navy"
          size="sm"
          disabled={!name.trim() || !!paramsError || save.isPending}
        >
          {save.isPending ? 'Saving…' : instance ? 'Save changes' : 'Add instance'}
        </Button>
      </div>
    </form>
  )
}

export default function GatewayDetailPage() {
  const { uid } = useParams<{ uid: string }>()
  const navigate = useNavigate()
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const isAdmin = useAuthStore((s) => s.customer?.role === 'admin')
  const queryClient = useQueryClient()

  const gateways = useEdgeGateways()
  const gateway = gateways.data?.find((g) => g.unique_id === uid)
  const allInstances = useEdgeInstances()
  // TODO(backend): GET /edge/instances has no `gateway` filter param — the
  // console fetches all customer instances and filters client-side.
  const instances = useMemo(
    () => (allInstances.data ?? []).filter((i) => i.gateway === uid),
    [allInstances.data, uid],
  )
  const logs = useInfiniteEdgeGatewayLogs(uid)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<EdgeInstance | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<EdgeInstance | null>(null)
  const [reinstallOpen, setReinstallOpen] = useState(false)
  const [aclEditOpen, setAclEditOpen] = useState(false)
  const [confirmDeleteGateway, setConfirmDeleteGateway] = useState(false)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })
  const restart = useMutation({ mutationFn: api.restartEdgeInstance })
  const remove = useMutation({
    mutationFn: api.deleteEdgeInstance,
    onSuccess: () => {
      invalidate()
      setConfirmDelete(null)
    },
  })
  const removeGateway = useMutation({
    mutationFn: api.deleteGateway,
    onSuccess: () => {
      invalidate()
      navigate('/gateways')
    },
  })

  const logItems = useMemo(() => logs.data?.pages.flatMap((p) => p.items) ?? [], [logs.data])

  if (gateways.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-28" />
        <Skeleton className="h-48" />
      </div>
    )
  }

  if (gateways.isError) {
    return <EmptyState title="Could not load gateways" hint={gateways.error.message} />
  }

  if (!gateway) {
    return (
      <EmptyState
        title="Gateway not found"
        hint={`No gateway with id ${uid ?? ''} on this customer.`}
        action={
          <Button variant="ghost" onClick={() => navigate('/gateways')}>
            ← Back to gateways
          </Button>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Edge"
        title={gateway.name}
        action={
          isAdmin ? (
            <Button
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              + Add instance
            </Button>
          ) : undefined
        }
      >
        <p className="tnum mt-1 text-xs text-ostrel-ink-muted">
          <Link to="/gateways" className="hover:text-ostrel-gold">
            Gateways
          </Link>
          {' / '}
          {gateway.unique_id}
        </p>
      </PageHeader>

      <Card title="Gateway">
        <div className="flex flex-wrap items-center gap-2">
          <ConnDot connected={gateway.connected} />
          <Chip tone={gateway.connected ? 'ok' : 'danger'} label={gateway.connected ? 'Connected' : 'Disconnected'} />
          <span className="tnum font-mono text-xs text-ostrel-ink-muted">{gateway.unique_id}</span>
        </div>
        <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2 border-t border-ostrel-line pt-3">
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Agent version
            </dt>
            <dd className="tnum text-xs text-ostrel-ink">{gateway.agent_version || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Cert fingerprint
            </dt>
            <dd className="tnum font-mono text-xs text-ostrel-ink">
              {gateway.cert_fingerprint || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Created
            </dt>
            <dd className="tnum text-xs text-ostrel-ink">
              {new Date(gateway.created_at).toLocaleString()}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Allowed source IPs
            </dt>
            <dd className="text-xs text-ostrel-ink">
              {gateway.allowed_ips.length === 0 ? (
                'Any source'
              ) : (
                <span className="flex flex-wrap gap-1">
                  {gateway.allowed_ips.map((cidr) => (
                    <Chip key={cidr} tone="muted" label={cidr} />
                  ))}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Last heartbeat
            </dt>
            <dd className="tnum text-xs text-ostrel-ink">
              {gateway.last_heartbeat_at
                ? `${new Date(gateway.last_heartbeat_at).toLocaleString()} (${relativeTime(gateway.last_heartbeat_at)})`
                : 'never connected'}
            </dd>
          </div>
        </dl>
        {!gateway.connected && (
          <p className="mt-3 text-[11px] leading-relaxed text-ostrel-ink-muted">
            This gateway has no live connection. Boot the appliance on the customer's network and
            enroll it with the one-time token from provisioning.
          </p>
        )}
        {isAdmin && (
          <div className="mt-3 flex gap-2 border-t border-ostrel-line pt-3">
            <Button variant="ghost" size="sm" onClick={() => setAclEditOpen(true)}>
              Edit source IPs
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setReinstallOpen(true)}>
              Reinstall
            </Button>
            <Button variant="danger" size="sm" onClick={() => setConfirmDeleteGateway(true)}>
              Delete gateway
            </Button>
          </div>
        )}
      </Card>

      <MetricsCard gateway={gateway} />

      <Card
        title="Instances"
        action={
          <span className="tnum text-[11px] text-ostrel-ink-muted">
            {instances.length} configured
          </span>
        }
      >
        {allInstances.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : allInstances.isError ? (
          <EmptyState title="Could not load instances" hint={allInstances.error.message} />
        ) : instances.length === 0 ? (
          <EmptyState
            title="No instances configured"
            hint="Instances are the pollers and relays this appliance runs — e.g. an HTTP relay to a LAN-bound panel."
          />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Unique id</TH>
                <TH>Type</TH>
                <TH>Name</TH>
                <TH>Active</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {instances.map((inst) => (
                <TR key={inst.unique_id}>
                  <TD className="tnum font-mono text-xs text-ostrel-ink-muted">{inst.unique_id}</TD>
                  <TD className="text-xs text-ostrel-ink">{instanceTypeLabel(inst.type)}</TD>
                  <TD className="text-sm font-medium text-ostrel-ink">{inst.name}</TD>
                  <TD>
                    <Chip tone={inst.active ? 'ok' : 'muted'} label={inst.active ? 'Active' : 'Standby'} />
                  </TD>
                  <TD className="text-right">
                    <span className="flex items-center justify-end gap-1.5">
                      {isRetiredInstanceType(inst.type) ? (
                        // Retired type: no worker runs it and the API refuses it,
                        // so Edit would silently convert it — Delete only.
                        <span className="text-xs text-ostrel-ink-muted">Retired type</span>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={restart.isPending || !isAdmin}
                            title={isAdmin ? undefined : 'Requires the admin role'}
                            onClick={() => restart.mutate(inst.unique_id)}
                          >
                            Restart
                          </Button>
                          {isManualInstanceType(inst.type) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!isAdmin}
                              title={isAdmin ? undefined : 'Requires the admin role'}
                              onClick={() => {
                                setEditing(inst)
                                setFormOpen(true)
                              }}
                            >
                              Edit
                            </Button>
                          ) : (
                            // Materialized by Ostrel from the System (stream_relay,
                            // axema_forwarder): the form can't represent the type,
                            // so saving would convert it. Edit the System instead.
                            <span className="text-xs text-ostrel-ink-muted" title="Created from the system's settings">
                              Managed
                            </span>
                          )}
                        </>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={!isAdmin}
                        title={isAdmin ? undefined : 'Requires the admin role'}
                        onClick={() => setConfirmDelete(inst)}
                      >
                        Delete
                      </Button>
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        {restart.error && (
          <p className="mt-3 rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
            {errDetail(restart.error)}
          </p>
        )}
        {restart.isSuccess && (
          <p className="mt-3 rounded-sm border border-ostrel-ok/40 bg-ostrel-ok/10 px-3 py-2 text-xs text-ostrel-ok">
            Restart command sent ({restart.data.command_id}).
          </p>
        )}
      </Card>

      <DoorHealthCard instances={instances} connected={gateway.connected} />

      <Card title="Logs">
        {logs.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : logs.isError ? (
          <EmptyState title="Could not load logs" hint={errDetail(logs.error)} />
        ) : logItems.length === 0 ? (
          <EmptyState
            title="No log entries"
            hint="Entries appear once the appliance is connected and reporting."
          />
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <TH>Time</TH>
                  <TH>Unit</TH>
                  <TH>Message</TH>
                </tr>
              </THead>
              <TBody>
                {logItems.map((entry) => (
                  <TR key={entry.unique_id}>
                    <TD>
                      <span className="tnum block font-mono text-[11px] text-ostrel-ink">
                        {timeOfDay(entry.ts)}
                      </span>
                      <span className="tnum block text-[10px] text-ostrel-ink-muted">
                        {localDate(entry.ts)}
                      </span>
                    </TD>
                    <TD className="tnum font-mono text-[11px] text-ostrel-ink-muted">
                      {entry.unit || '—'}
                    </TD>
                    <TD className="text-xs text-ostrel-ink">{entry.message}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            {logs.hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="ghost"
                  disabled={logs.isFetchingNextPage}
                  onClick={() => logs.fetchNextPage()}
                >
                  {logs.isFetchingNextPage ? 'Loading…' : '← Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {formOpen && (
        <Modal
          open
          title={editing ? `Edit instance ${editing.unique_id}` : 'Add instance'}
          onClose={() => setFormOpen(false)}
        >
          <InstanceForm gateway={gateway.unique_id} instance={editing} onDone={() => setFormOpen(false)} />
        </Modal>
      )}

      <ConfirmDeleteModal
        open={confirmDelete !== null}
        title="Delete instance"
        objectName={confirmDelete?.name ?? ''}
        expected={confirmDelete?.unique_id ?? ''}
        pending={remove.isPending}
        error={remove.error ? errDetail(remove.error) : null}
        confirmLabel="Delete instance"
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.unique_id)}
        onClose={() => setConfirmDelete(null)}
      >
        <p>
          Remove <strong>{confirmDelete?.name}</strong> ({confirmDelete?.unique_id}) from this
          gateway? The appliance stops running it on its next spec update.
        </p>
      </ConfirmDeleteModal>

      {reinstallOpen && (
        <ReinstallGatewayModal gatewayUid={gateway.unique_id} onClose={() => setReinstallOpen(false)} />
      )}

      {aclEditOpen && <EditAllowedIpsModal gateway={gateway} onClose={() => setAclEditOpen(false)} />}

      <ConfirmDeleteModal
        open={confirmDeleteGateway}
        title="Delete gateway"
        objectName={gateway.name}
        expected={gateway.unique_id}
        pending={removeGateway.isPending}
        error={removeGateway.error ? errDetail(removeGateway.error) : null}
        confirmLabel="Delete gateway"
        onConfirm={() => removeGateway.mutate(gateway.unique_id)}
        onClose={() => setConfirmDeleteGateway(false)}
      >
        <p>
          Delete gateway <strong>{gateway.name}</strong> ({gateway.unique_id})? Its certificate
          stops resolving immediately — this cannot be undone. The backend refuses while the
          gateway is connected or still has instances.
        </p>
      </ConfirmDeleteModal>
    </div>
  )
}
